import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  CompassCardComponent,
  CompassCardComponentProps,
} from 'react-scale/corp/views/DataCompassV2/types';
import { useCompassCard } from '@scale/compass-ui/store';
import {
  Select,
  TextField,
  Heading,
  Text,
  Badge,
  TextArea,
  Flex,
  Button,
  Callout,
  Spinner,
  Icon,
  Code,
} from '@scale/scaleui-radix';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import type { ResponseBackfillContext } from '@scale/genai-compass-shared/cards/response_backfill';
import YamlDiffView from '@scale/compass-ui/components/YamlDiffView';
import { Column, ColumnSubType } from '@scale/compass-shared/types';
import apiFetch from 'react-scale/lib/api-fetch';

/**
 * Deep clone an object
 */
const deepClone = <T,>(obj: T): T => JSON.parse(JSON.stringify(obj));

/**
 * Set a nested value in an object using a dot-notation path
 * Supports array indexing like "turns[0].prompt"
 */
const setNestedValue = (obj: any, path: string, value: any): void => {
  const parts = path.split('.').flatMap(part => {
    // Handle array notation like "turns[0]"
    const match = part.match(/^([^[]+)\[(\d+)\]$/);
    if (match) {
      return [match[1], parseInt(match[2], 10)];
    }
    return [part];
  });

  let current = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    if (current[part] === undefined) {
      current[part] = typeof parts[i + 1] === 'number' ? [] : {};
    }
    current = current[part];
  }
  current[parts[parts.length - 1]] = value;
};

/**
 * Check if a string is a valid non-negative integer (or empty, which defaults to 0)
 */
const isValidTurnIndex = (value: string): boolean => {
  if (!value || value.trim() === '') return true; // Empty is valid, defaults to 0
  const num = parseInt(value, 10);
  return !isNaN(num) && num >= 0 && num.toString() === value.trim();
};

/**
 * Interpolate {{variable}} placeholders in a template string with values from a data row
 */
const interpolateTemplate = (template: string, rowData: Record<string, any>): string => {
  return template.replace(/\{\{(\w+)\}\}/g, (match, variableName) => {
    const value = rowData[variableName];
    if (value === undefined || value === null) {
      return match; // Keep the placeholder if value not found
    }
    // Convert value to string
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
  });
};

/**
 * Configuration for available sections, step IDs, and fields
 */
type FieldConfig = {
  value: string;
  label: string;
};

type StepConfig = {
  value: string;
  label: string;
  fields: FieldConfig[];
};

type SectionConfig = {
  value: string;
  label: string;
  requiresIndex: boolean; // Whether this section needs a turn index
  steps: StepConfig[];
};

// Available sections and their configurations
const SECTION_CONFIG: SectionConfig[] = [
  {
    value: 'before',
    label: 'before',
    requiresIndex: false,
    steps: [
      {
        value: 'prompt',
        label: 'prompt',
        fields: [{ value: 'output.content', label: 'output.content' }],
      },
    ],
  },
  {
    value: 'turns',
    label: 'turns',
    requiresIndex: true,
    steps: [
      {
        value: 'prompt',
        label: 'prompt',
        fields: [{ value: 'output.content', label: 'output.content' }],
      },
    ],
  },
  {
    value: 'after',
    label: 'after',
    requiresIndex: false,
    steps: [
      {
        value: 'prompt',
        label: 'prompt',
        fields: [{ value: 'output.content', label: 'output.content' }],
      },
    ],
  },
];

// eslint-disable-next-line complexity
const ResponseBackfillCardComponent: CompassCardComponent = ({
  index,
}: CompassCardComponentProps) => {
  const { card, updateCardContext } = useCompassCard<ResponseBackfillContext>(index);
  const context = card.defaultContext;
  const [showPreview, setShowPreview] = useState(false);
  const [isLoadingResponse, setIsLoadingResponse] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [originalJson, setOriginalJson] = useState<any>(null);
  const [updatedJson, setUpdatedJson] = useState<any>(null);
  const [taskResponse, setTaskResponse] = useState<any>(null);
  const [responseError, setResponseError] = useState<string | null>(null);
  const { retrievedOutput } = useCompassCard(index - 1);

  // Get column names from the previous card's output
  const columnNames = useMemo(() => {
    const schema = retrievedOutput?.response?.schema as Column[] | undefined;
    if (!schema) return [];
    return schema.map((col: Column) => col.header);
  }, [retrievedOutput]);

  // Get the first row of data
  const firstRowData = useMemo(() => {
    return retrievedOutput?.response?.data?.[0] || null;
  }, [retrievedOutput]);

  // Find the taskId column name
  const taskIdColumnName = useMemo(() => {
    const schema = retrievedOutput?.response?.schema as Column[] | undefined;
    if (!schema) return null;
    const taskIdCol = schema.find((col: Column) => col.subType === ColumnSubType.TASK_ID);
    return taskIdCol?.header || 'taskId';
  }, [retrievedOutput]);

  // Validate turn index (only show error if there's a non-empty invalid value)
  const turnIndexError =
    context.turnIndex && context.turnIndex.trim() !== '' && !isValidTurnIndex(context.turnIndex);

  // Get the taskId from the first row
  const taskId = useMemo(() => {
    if (!firstRowData || !taskIdColumnName) return null;
    return firstRowData[taskIdColumnName];
  }, [firstRowData, taskIdColumnName]);

  // Fetch the task response when taskId changes
  useEffect(() => {
    if (!taskId) {
      setTaskResponse(null);
      setResponseError(null);
      return;
    }

    const fetchTaskResponse = async () => {
      setIsLoadingResponse(true);
      setResponseError(null);
      try {
        const task = await apiFetch<any>(null, `corp-api/tasks/${taskId}`);
        if (task?.response) {
          setTaskResponse(task.response);
        } else {
          setResponseError(`Task ${taskId} does not have a valid response.`);
          setTaskResponse(null);
        }
      } catch (err) {
        setResponseError(
          `Failed to fetch task: ${err instanceof Error ? err.message : 'Unknown error'}`,
        );
        setTaskResponse(null);
      } finally {
        setIsLoadingResponse(false);
      }
    };

    fetchTaskResponse();
  }, [taskId]);

  // Get current section config
  const currentSectionConfig = useMemo(() => {
    return SECTION_CONFIG.find(s => s.value === (context.section || 'turns')) || SECTION_CONFIG[1];
  }, [context.section]);

  // Get allowed steps from config for the current section
  const allowedStepIds = useMemo(() => {
    return new Set(currentSectionConfig.steps.map((s: StepConfig) => s.value));
  }, [currentSectionConfig]);

  // Extract step IDs from the task response based on section and turn index
  const responseStepIds = useMemo(() => {
    if (!taskResponse) return [];

    const section = context.section || 'turns';
    const turnIndex = parseInt(context.turnIndex || '0', 10);

    let sectionData: any;
    if (currentSectionConfig.requiresIndex) {
      // For turns, access the array element
      sectionData = taskResponse[section]?.[turnIndex];
    } else {
      // For before/after, access directly
      sectionData = taskResponse[section];
    }

    if (!sectionData || typeof sectionData !== 'object') return [];

    // Get all keys that are step IDs (objects with step data)
    return Object.keys(sectionData).filter(key => {
      const value = sectionData[key];
      return value && typeof value === 'object' && !Array.isArray(value);
    });
  }, [taskResponse, context.section, context.turnIndex, currentSectionConfig]);

  // Combine response steps with config - show all from response, mark enabled/disabled
  const stepsWithStatus = useMemo(() => {
    return responseStepIds.map((stepId: string) => ({
      value: stepId,
      label: stepId,
      enabled: allowedStepIds.has(stepId),
    }));
  }, [responseStepIds, allowedStepIds]);

  // Get current step config from allowed steps
  const currentStepConfig = useMemo(() => {
    return (
      currentSectionConfig.steps.find(
        (s: StepConfig) => s.value === (context.stepId || 'prompt'),
      ) || currentSectionConfig.steps[0]
    );
  }, [currentSectionConfig, context.stepId]);

  // Get available fields for the current step
  const availableFields = useMemo(() => {
    return currentStepConfig?.fields || [];
  }, [currentStepConfig]);

  // Whether the current section requires a turn index
  const requiresTurnIndex = currentSectionConfig.requiresIndex;

  const handleSectionChange = (value: string) => {
    const newSectionConfig = SECTION_CONFIG.find((s: SectionConfig) => s.value === value);
    const firstStep = newSectionConfig?.steps[0];
    const firstField = firstStep?.fields[0];
    updateCardContext({
      ...context,
      section: value,
      stepId: firstStep?.value || 'prompt',
      field: firstField?.value || 'output.content',
      turnIndex: newSectionConfig?.requiresIndex ? context.turnIndex : '', // Clear turn index if not needed
    });
  };

  const handleStepIdChange = (value: string) => {
    const newStepConfig = currentSectionConfig.steps.find(s => s.value === value);
    const firstField = newStepConfig?.fields[0];
    updateCardContext({
      ...context,
      stepId: value,
      field: firstField?.value || 'output.content',
    });
  };

  const handleFieldChange = (value: string) => {
    updateCardContext({ ...context, field: value });
  };

  const handleTurnIndexChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateCardContext({ ...context, turnIndex: e.target.value });
  };

  const handleNewValueTemplateChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateCardContext({ ...context, newValueTemplate: e.target.value });
  };

  const insertVariable = (variable: string) => {
    const newValue = (context.newValueTemplate || '') + `{{${variable}}}`;
    updateCardContext({ ...context, newValueTemplate: newValue });
  };

  const handlePreview = useCallback(async () => {
    setPreviewError(null);
    setShowPreview(false);

    // Validate turn index (only if section requires it)
    const sectionRequiresIndex = SECTION_CONFIG.find(
      (s: SectionConfig) => s.value === (context.section || 'turns'),
    )?.requiresIndex;
    if (sectionRequiresIndex && !isValidTurnIndex(context.turnIndex || '')) {
      setPreviewError('Turn index must be a non-negative integer.');
      return;
    }

    if (!firstRowData) {
      setPreviewError('No data available. Add a data source card first.');
      return;
    }

    if (!taskId) {
      setPreviewError('No taskId found in the data.');
      return;
    }

    setIsLoadingPreview(true);

    try {
      // Re-fetch the latest task response
      const task = await apiFetch<any>(null, `corp-api/tasks/${taskId}`);

      if (!task?.response) {
        setPreviewError(`Task ${taskId} does not have a valid response.`);
        return;
      }

      const original = task.response;
      setOriginalJson(original);

      // Create updated copy
      const updated = deepClone(original);
      const section = context.section || 'turns';
      const turnIndex = parseInt(context.turnIndex || '0', 10);
      const stepId = context.stepId || 'prompt';
      const field = context.field || 'output.content';

      // Build the path based on section type
      // For "turns": <section>[<turn index>].<step ID>.<field>
      // For "before"/"after": <section>.<step ID>.<field>
      const sectionConfig = SECTION_CONFIG.find((s: SectionConfig) => s.value === section);
      const path = sectionConfig?.requiresIndex
        ? `${section}[${turnIndex}].${stepId}.${field}`
        : `${section}.${stepId}.${field}`;

      // Interpolate the template with actual values from the first row
      const interpolatedValue = interpolateTemplate(context.newValueTemplate || '', firstRowData);
      setNestedValue(updated, path, interpolatedValue);
      setUpdatedJson(updated);

      setShowPreview(true);
    } catch (err) {
      setPreviewError(
        `Failed to fetch task: ${err instanceof Error ? err.message : 'Unknown error'}`,
      );
    } finally {
      setIsLoadingPreview(false);
    }
  }, [context, firstRowData, taskId]);

  return (
    <div className="flex flex-col gap-6">
      {/* Task field to update section */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <Heading size="3" weight="medium">
            Task field to update
          </Heading>
          <Text size="2" color="gray">
            Select the existing field you want to update in the task.
          </Text>
        </div>

        <div className="flex flex-row flex-wrap gap-4">
          <div className="flex flex-col gap-2">
            <Text size="2" weight="medium">
              Section
            </Text>
            <Select.Root value={context.section || 'turns'} onValueChange={handleSectionChange}>
              <Select.Trigger className="w-32" />
              <Select.Content>
                {SECTION_CONFIG.map(section => (
                  <Select.Item key={section.value} value={section.value}>
                    {section.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </div>

          {requiresTurnIndex && (
            <div className="flex flex-col gap-2">
              <Text size="2" weight="medium">
                Turn Index
              </Text>
              <TextField.Root
                className="w-20"
                value={context.turnIndex || ''}
                onChange={handleTurnIndexChange}
                placeholder="0"
                type="number"
                min={0}
                color={turnIndexError ? 'red' : undefined}
              />
              {turnIndexError && (
                <Text size="1" color="red">
                  Must be a non-negative integer
                </Text>
              )}
            </div>
          )}

          <div className="flex flex-col gap-2">
            <Text size="2" weight="medium">
              Step ID
            </Text>
            <Select.Root
              value={context.stepId || 'prompt'}
              onValueChange={handleStepIdChange}
              disabled={isLoadingResponse}
            >
              <Select.Trigger className="w-48" />
              <Select.Content>
                {isLoadingResponse ? (
                  <Select.Item value="loading" disabled>
                    Loading...
                  </Select.Item>
                ) : stepsWithStatus.length > 0 ? (
                  stepsWithStatus.map(
                    (step: { value: string; label: string; enabled: boolean }) => (
                      <Select.Item key={step.value} value={step.value} disabled={!step.enabled}>
                        {step.label}
                      </Select.Item>
                    ),
                  )
                ) : (
                  <Select.Item value="prompt">prompt</Select.Item>
                )}
              </Select.Content>
            </Select.Root>
            {responseError && (
              <Text size="1" color="red">
                {responseError}
              </Text>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Text size="2" weight="medium">
              Field
            </Text>
            <Select.Root
              value={context.field || 'output.content'}
              onValueChange={handleFieldChange}
            >
              <Select.Trigger className="w-48" />
              <Select.Content>
                {availableFields.map((field: FieldConfig) => (
                  <Select.Item key={field.value} value={field.value}>
                    {field.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </div>
        </div>
      </div>

      {/* New value section */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <Heading size="3" weight="medium">
            New value
          </Heading>
          <Text size="2" color="gray">
            Input the new value that you want for this field
          </Text>
        </div>

        <div className="flex flex-col gap-3">
          <Flex gap="2" wrap="wrap">
            {columnNames.length > 0 ? (
              columnNames.map((columnName: string) => (
                <Badge
                  key={columnName}
                  size="2"
                  color="accent"
                  className="cursor-pointer hover:opacity-80"
                  onClick={() => insertVariable(columnName)}
                >
                  {columnName}
                </Badge>
              ))
            ) : (
              <Text size="2" color="gray">
                No columns available. Add a data source card first.
              </Text>
            )}
          </Flex>

          <TextArea
            className="w-full"
            value={context.newValueTemplate || ''}
            onChange={handleNewValueTemplateChange}
            placeholder="Enter the new value template. Use {{variables}} or click the chips above to insert dynamic values."
            rows={6}
          />

          <Flex align="center" gap="3">
            <Button
              variant="solid"
              onClick={handlePreview}
              className="w-fit"
              disabled={isLoadingPreview || !taskId}
            >
              {isLoadingPreview ? (
                <>
                  <Spinner size="1" /> Loading...
                </>
              ) : (
                'Preview'
              )}
            </Button>
            <div className="flex items-center gap-2">
              <Icon icon={faCircleInfo} className="text-gray-400" size="1" label="Info" />
              <Text size="1" color="gray">
                Using column <Code>{taskIdColumnName || 'taskId'}</Code> to identify tasks
              </Text>
            </div>
          </Flex>

          {previewError && (
            <Callout.Root color="red" size="1">
              <Callout.Text>{previewError}</Callout.Text>
            </Callout.Root>
          )}
        </div>
      </div>

      {/* Preview change section */}
      {showPreview && originalJson && updatedJson && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <Heading size="3" weight="medium">
              Preview change
            </Heading>
            <Text size="2" color="gray">
              See how your changes will affect the task response
            </Text>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <YamlDiffView
              oldVersion={JSON.stringify(originalJson, null, 2)}
              newVersion={JSON.stringify(updatedJson, null, 2)}
              leftTitle="Original Response"
              rightTitle="Updated Response"
            />
          </div>
        </div>
      )}
    </div>
  );
};

ResponseBackfillCardComponent.displayName = 'ResponseBackfillCardComponent';

export default ResponseBackfillCardComponent;
